import type { ContractTurnaroundReading } from '@console/types'

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE SPEED SECTION'S OWN SUMMARY — `LGH-029` §2, `PLT-054`.
 *
 * `PLT-054` serves `turnaround` per case and explicitly refuses to compute a
 * tenant-level average itself: *"a distribution is a different fact from a
 * case metric, and whoever needs it will ask for the shape they need."* This
 * is that — computed here, from the per-case facts the console already
 * fetches (`REQ-lighthouse-the-turnaround-wire-shape` option (2)), not a
 * platform policy re-implemented client-side.
 *
 * ── NEVER MIX PRECISION ──────────────────────────────────────────────────
 * `hours` and `days` readings are summarised separately and never merged
 * into one median — exactly the "prints hours over a days-precision number"
 * failure the wire shape's own discriminated union exists to make a type
 * error rather than a rounding choice. `days` has no producer today
 * (`TurnaroundReading`'s own header), so that bucket is expected to stay
 * empty; the code does not assume it will.
 * ═══════════════════════════════════════════════════════════════════════════
 */

export interface TurnaroundBucket {
  count: number
  /** In this bucket's OWN unit — hours in `.hours`, days in `.days`. Never cross-compared. */
  median: number
}

export interface TurnaroundSummary {
  sampleSize: number
  measuredCount: number
  hours: TurnaroundBucket | null
  days: TurnaroundBucket | null
}

function median(values: readonly number[]): number {
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  const midValue = sorted[mid]
  if (midValue === undefined) {
    throw new Error('median() called with an empty array')
  }
  if (sorted.length % 2 === 1) return midValue
  const lower = sorted[mid - 1]
  return lower === undefined ? midValue : (lower + midValue) / 2
}

function bucketOf(values: readonly number[]): TurnaroundBucket | null {
  if (values.length === 0) return null
  return { count: values.length, median: median(values) }
}

/**
 * `runs` is whatever sample the caller fetched — this makes no claim about
 * being every run the tenant has, and `sampleSize` is carried through so the
 * screen can say so rather than presenting a median as if it were global.
 */
export function summarizeTurnaround(
  runs: readonly { turnaround: ContractTurnaroundReading | null }[],
): TurnaroundSummary {
  const hoursValues: number[] = []
  const daysValues: number[] = []

  for (const run of runs) {
    if (run.turnaround === null) continue
    if (run.turnaround.precision === 'hours') hoursValues.push(run.turnaround.hours)
    else daysValues.push(run.turnaround.days)
  }

  return {
    sampleSize: runs.length,
    measuredCount: hoursValues.length + daysValues.length,
    hours: bucketOf(hoursValues),
    days: bucketOf(daysValues),
  }
}
